import type { IComponentController, IScope } from "angular";
import "angular-translate";
import { finalize } from "rxjs/operators";
import "@progress/kendo-ui/js/kendo.autocomplete";

import { cloneDeep, isEmpty } from "lodash";

import type { IAccountRepository } from "App/Modules/Auth";
import type { IEnumService, ILabel, IResponseWithLoading, IRole, ISite, ITeamMember } from "App/Modules/Models";
import { RoleFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

export default class EditUserOnSiteComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "irRolesRepository",
        "mmSitesRepository",
        "mmPartnerUsersRepository",
        "mmEnumService",
        "mmAccountRepository",
    ];

    public partnerId: string;

    public site: ISite;

    public user: ITeamMember;

    public originalUser: ITeamMember;

    public errorMessage: string;

    public editResponse: IResponseWithLoading<ITeamMember>;

    public deleteResponse: IResponseWithLoading<void>;

    public siteRoles: IResponseWithLoading<IRole[]>;

    public labels: string[];

    public partnerUserLabelsString: string;

    public originalPartnerUserLabelsString: string;

    public resolve: IFormContainerResolve<ITeamMember, ISite>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly rolesRepository: IRolesRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly enumService: IEnumService,
        private readonly accountRepository: IAccountRepository,
    ) {}

    public $onInit() {
        this.originalUser = this.resolve.formData;
        this.site = this.resolve.bindings;
        this.partnerId = this.resolve.bindings.partner.id;
        this.user = cloneDeep(this.originalUser);

        this.partnerUserLabelsString =
            this.user.labels.map((l) => l.label).join(",") + (this.user.labels.length > 0 ? "," : "");
        this.originalPartnerUserLabelsString = cloneDeep(this.partnerUserLabelsString);

        extendWithLoadingAndFail(this.rolesRepository.getSiteRoles())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        const roles = [this.rolesRepository.getNoRole()].concat(response.data);
                        response.data = roles;
                    }
                    this.siteRoles = response;
                },
                (response) => {
                    this.siteRoles = response;
                },
            );

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.partnerId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                if (!response.hasError && response.data) {
                    this.labels = response.data.map((l) => l.label);
                }
            });
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public editUser() {
        const patchObject = {} as ITeamMember;

        if (this.originalPartnerUserLabelsString !== this.partnerUserLabelsString) {
            patchObject.labels = this.partnerUserLabelsString
                .split(",")
                .map((l) => l.trim())
                .filter((l) => l !== "")
                .map((l) => ({ label: l } as ILabel));
        }

        if (
            (this.originalUser.role === null && this.user.role.id !== null) ||
            (this.originalUser.role !== null && this.originalUser.role.id !== this.user.role.id)
        ) {
            patchObject.role = this.user.role && this.user.role.id ? ({ id: this.user.role.id } as IRole) : null;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.sitesRepository.patchUser(this.site.id, this.user.partnerUser.id, patchObject),
            ).subscribe(
                (response: IResponseWithLoading<ITeamMember>) => {
                    this.editResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data);
                    }
                },
                (errorResponse: IResponseWithLoading<ITeamMember>) => {
                    this.editResponse = errorResponse;
                },
            );
        } else {
            this.cancel();
        }
    }

    public deleteUser() {
        extendWithLoadingAndFail(
            this.sitesRepository.removeUserFromSite(this.site.id, this.user.partnerUser.id),
        ).subscribe((response: IResponseWithLoading<void>) => {
            this.deleteResponse = response;
            if (!response.isLoading && !response.hasError) {
                this.resolve.options.onOk(null);
            }
        });
    }
}
